import { useState } from 'react';
import { getBridge } from '@/platform';
import { lessonFileName } from '@/lib/fileName';
import { useAiChatStore } from '@/store/aiChatStore';
import { useUiStore } from '@/store/uiStore';
import type { AiChatAttachment } from '@/api/miniapp/types';

/** Открытый файл: адрес blob живёт только пока просмотр открыт. */
interface OpenAttachment {
  url: string;
  name: string;
  contentType?: string;
}

/**
 * Просмотр и сохранение вложения из переписки.
 *
 * Файл скачивается через прокси бэкенда с cookie сессии, поэтому смотреть его
 * можно только из приложения: прямую ссылку сервер не отдаст.
 */
export function useAttachmentPreview(): {
  file: OpenAttachment | null;
  open(attachment: AiChatAttachment): Promise<void>;
  close(): void;
  save(): Promise<void>;
} {
  const toast = useUiStore((state) => state.toast);
  const [opened, setOpened] = useState<{
    url: string;
    blob: Blob;
    attachment: AiChatAttachment;
  } | null>(null);

  const load = async (attachment: AiChatAttachment) => {
    const blob = await useAiChatStore.getState().downloadAttachment(attachment);
    if (!blob) toast('Вложение недоступно', 'error');
    return blob;
  };

  return {
    file: opened
      ? {
          url: opened.url,
          // В заголовке просмотра и при сохранении — понятное имя с расширением
          name: lessonFileName({ name: opened.attachment.filename }, 0),
          contentType: opened.attachment.content_type,
        }
      : null,

    async open(attachment) {
      const blob = await load(attachment);
      if (blob) setOpened({ url: URL.createObjectURL(blob), blob, attachment });
    },

    close() {
      if (opened) URL.revokeObjectURL(opened.url);
      setOpened(null);
    },

    async save() {
      if (!opened) return;

      // Файл уже загружен для просмотра. Повторный запрос перед вызовом моста
      // отодвигал бы его от нажатия, а MAX без свежего нажатия загрузку молча
      // не выполняет.
      //
      // Адрес намеренно передаётся относительным: нативная загрузка Telegram
      // качает файл сама, без cookie сессии, и прокси ответил бы ей отказом.
      // Относительный путь уводит мост в ветку с готовым blob
      const ok = await getBridge().downloadFile({
        url: opened.attachment.url,
        fileName: lessonFileName({ name: opened.attachment.filename }, 0),
        blob: opened.blob,
      });
      if (!ok) toast('Клиент запретил скачивание файла', 'error');
    },
  };
}
